import type { ScopeFilter } from '@@/api/health';
import type { ProgramAnalyticsResponse, ProgramOutcome } from '@@/api/program';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';
import { useOrganizations } from '../useOrganizations';
import { summariseProgram, windowStart } from './program-summary';

export const PROGRAM_WINDOWS = [3, 6, 12, 24] as const;
export type ProgramWindow = (typeof PROGRAM_WINDOWS)[number];

export type OutcomeFilter = ProgramOutcome | 'all';

export const PROGRAM_PAGE_SIZE = 25;

/**
 * The programme screen's state: which slice, which window, and what the list shows.
 *
 * The window is whole local months ending with the current one, so "last 12 months"
 * always has twelve complete-or-current bars instead of a ragged first bucket. Its
 * start is sent as an instant, which keeps the server free of any timezone opinion.
 *
 * Aggregation runs here rather than on the server: the rows are a few hundred at
 * most, and the month buckets are the viewer's months.
 */
export const useProgramAnalytics = () => {
  const client = useClient();
  const { organization } = useOrganizations();

  const [scope, setScopeState] = useState<ScopeFilter>('all');
  const [months, setMonthsState] = useState<ProgramWindow>(12);
  const [outcome, setOutcomeState] = useState<OutcomeFilter>('all');
  const [search, setSearchState] = useState('');
  const [page, setPage] = useState(1);

  // Each filter change starts the list again from its first page, or a narrowed list
  // can leave the reader on a page past its end.
  const setScope = (next: ScopeFilter) => {
    setScopeState(next);
    setPage(1);
  };
  const setMonths = (next: ProgramWindow) => {
    setMonthsState(next);
    setPage(1);
  };
  const setOutcome = (next: OutcomeFilter) => {
    setOutcomeState(next);
    setPage(1);
  };
  const setSearch = (next: string) => {
    setSearchState(next);
    setPage(1);
  };

  const accountId = organization?.id ?? 0;
  const now = Date.now();
  const since = windowStart(months, now);

  const { data, isLoading, isError, refetch } = useQuery<ProgramAnalyticsResponse>({
    queryKey: ['PROGRAM_ANALYTICS', scope, scope === 'account' ? accountId : null, since],
    queryFn: () =>
      client.get<ProgramAnalyticsResponse>(`/program/analytics?scope=${scope}&accountId=${accountId}&since=${since}`),
  });

  // The server's `since`, not ours: it is the window actually applied after clamping.
  const summary = data ? summariseProgram({ rows: data.rows, since: data.since, now }) : null;

  const query = search.trim().toLowerCase();
  const listed = (summary?.concludedRows ?? []).filter(
    (row) =>
      (outcome === 'all' || row.reading?.outcome === outcome) &&
      (!query || row.name.toLowerCase().includes(query) || row.websiteUrl.toLowerCase().includes(query)),
  );

  const totalPages = Math.max(1, Math.ceil(listed.length / PROGRAM_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = listed.slice((currentPage - 1) * PROGRAM_PAGE_SIZE, currentPage * PROGRAM_PAGE_SIZE);

  return {
    isLoading,
    isError,
    refetch,
    isAgency: data?.isAgency ?? false,
    summary,

    scope,
    setScope,
    months,
    setMonths,
    outcome,
    setOutcome,
    search,
    setSearch,

    listedCount: listed.length,
    pageRows,
    currentPage,
    totalPages,
    setPage,
  };
};

export type ProgramAnalyticsManager = ReturnType<typeof useProgramAnalytics>;
